import { useState } from 'react';
import { Pencil, RotateCcw, SlidersHorizontal } from 'lucide-react';
import clsx from 'clsx';
import type { SqlParameter } from '@idas/shared';
import { useColumnOptions } from './useColumnOptions';

/**
 * The controls a saved query earns by having literals in it.
 *
 * The point is that "tutors active since 1 July" stops being a query somebody
 * has to duplicate every month. Each control is typed from the literal it
 * replaces, so a date gets a date picker and a row limit gets a number field —
 * the reader never types SQL, and never sees it.
 *
 * Values are applied on submit rather than on change: each apply is a real
 * query against a real database, and re-running on every keystroke would be
 * both slow and, in the audit log, indistinguishable from someone probing.
 */
export function ParameterBar({
  dataSourceId,
  parameters,
  values,
  onChange,
  onApply,
  onReset,
  dirty,
  running,
  error,
}: {
  dataSourceId: string;
  parameters: SqlParameter[];
  values: Record<string, string>;
  onChange: (id: string, value: string) => void;
  onApply: () => void;
  onReset: () => void;
  dirty: boolean;
  running: boolean;
  error: string | null;
}) {
  if (!parameters.length) return null;

  // The same column can be bounded twice in one query, which produces two
  // controls with the same name. Where that happens the saved value is shown
  // alongside, because "from 2026-07-01" and "from 2026-02-01" are only
  // telling apart by what they started as.
  const labelCounts = parameters.reduce<Record<string, number>>((acc, p) => {
    acc[p.label] = (acc[p.label] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <form
      className="mb-3 rounded-lg border border-edge bg-surface-raised p-3"
      onSubmit={(event) => {
        event.preventDefault();
        onApply();
      }}
    >
      <div className="mb-2 flex items-center gap-1.5">
        <SlidersHorizontal className="h-3 w-3 text-ink-muted" />
        <span className="text-2xs font-medium text-ink-muted">Filters</span>
        <span className="text-2xs text-ink-faint">
          taken from the values written into this query
        </span>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        {parameters.map((parameter) => (
          <label key={parameter.id} className="flex min-w-0 flex-col gap-1">
            <span className="truncate text-2xs text-ink-muted" title={parameter.column ?? undefined}>
              {parameter.label}
              {(labelCounts[parameter.label] ?? 0) > 1 && parameter.value && (
                <span className="ml-1 text-ink-faint">(was {parameter.value})</span>
              )}
            </span>
            <Control
              dataSourceId={dataSourceId}
              parameter={parameter}
              value={values[parameter.id] ?? parameter.value}
              onChange={(v) => onChange(parameter.id, v)}
            />
          </label>
        ))}

        <div className="flex items-center gap-1.5">
          <button type="submit" className="btn btn-primary" disabled={!dirty || running}>
            {running ? 'Running…' : 'Apply'}
          </button>
          {dirty && (
            <button
              type="button"
              className="btn btn-ghost px-1.5"
              onClick={onReset}
              title="Back to the values saved with this query"
              aria-label="Reset filters"
            >
              <RotateCcw className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      {error && (
        <p className="mt-2 text-2xs leading-relaxed text-danger">{error}</p>
      )}
    </form>
  );
}

function Control({
  dataSourceId,
  parameter,
  value,
  onChange,
}: {
  dataSourceId: string;
  parameter: SqlParameter;
  value: string;
  onChange: (value: string) => void;
}) {
  const common = clsx('input py-1', parameter.kind === 'text' ? 'w-52' : 'w-auto');

  switch (parameter.kind) {
    case 'date':
      return (
        <input
          type="date"
          className={common}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case 'datetime':
      return (
        <input
          type="datetime-local"
          className={common}
          // The input wants a T between date and time; MySQL wrote a space.
          value={value.replace(' ', 'T').slice(0, 16)}
          onChange={(e) => onChange(e.target.value.replace('T', ' '))}
        />
      );

    case 'number':
      return (
        <input
          type="number"
          className={clsx(common, 'w-28')}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case 'text':
      return (
        <TextControl
          dataSourceId={dataSourceId}
          parameter={parameter}
          value={value}
          onChange={onChange}
          className={common}
        />
      );
  }
}

/**
 * A text filter, as a list of the column's actual values where that is
 * knowable.
 *
 * `project name — is` is a choice, not a sentence: making someone type it
 * exactly, spelling and case included, is asking them to guess at what the
 * database happens to contain. Where the column can be placed in the catalog
 * its distinct values fill a dropdown instead.
 *
 * Typing stays available behind "Type a value". The list is capped and can be
 * stale, and a filter that can only offer yesterday's options is worse than
 * one that also lets you write your own.
 */
function TextControl({
  dataSourceId,
  parameter,
  value,
  onChange,
  className,
}: {
  dataSourceId: string;
  parameter: SqlParameter;
  value: string;
  onChange: (value: string) => void;
  className: string;
}) {
  const [freeText, setFreeText] = useState(false);
  // `IS NOT` and `LIKE` are usually patterns rather than exact values, so a
  // list of exact values would be the wrong offer.
  const wantsList = parameter.operator === '=' || parameter.operator === 'IN';
  const { options, loading, source } = useColumnOptions(
    dataSourceId,
    parameter.column,
    wantsList,
  );

  if (freeText || (!loading && options.length === 0)) {
    return (
      <input
        type="text"
        className={className}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  if (loading) {
    return (
      <div className={clsx(className, 'flex items-center text-ink-faint')}>
        <span className="truncate">Loading values…</span>
      </div>
    );
  }

  // The saved value may no longer be in the column — keep it selectable so
  // opening the page never silently changes what the query asked for.
  const withCurrent = options.includes(value) ? options : [value, ...options];

  return (
    <span className="flex items-center gap-1">
      <select
        className={className}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        title={source ? `Values from ${source}` : undefined}
      >
        {withCurrent.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="btn btn-ghost px-1"
        onClick={() => setFreeText(true)}
        title="Type a value instead"
        aria-label="Type a value instead"
      >
        <Pencil className="h-3 w-3" />
      </button>
    </span>
  );
}
